import React, { useEffect, useMemo, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import { useStaticOptions } from "../context/StaticOptionsContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";

const Rama = () => {
    const { order, updateOrder } = useOrder();
    const navigate = useStepNavigate();
    const { staticOptions } = useStaticOptions();

    const ramaRows = staticOptions?.rama_types_and_colors || [];
    const isOpenNosedModel = Number(order?.model_options?.is_open_nosed) === 1;

    const filteredRows = useMemo(() => {
        return ramaRows.filter((row) => {
            if (isOpenNosedModel && Number(row?.rmeliramacolors_is_for_open_nosed) === 0) {
                return false;
            }
            return true;
        });
    }, [ramaRows, isOpenNosedModel]);

    const typeOptions = useMemo(() => {
        const map = new Map();

        filteredRows.forEach((row) => {
            const typeId = String(row?.rmeliramacolors_rama_type_id ?? "");
            const typeName = row?.rmeliramacolors_rama_type_national || "";

            if (!typeId || !typeName) return;
            if (!map.has(typeId)) {
                map.set(typeId, { value: typeId, label: typeName });
            }
        });

        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "hu"));
    }, [filteredRows]);

    const selectedFromOrder = useMemo(() => {
        const selectedId = order?.rama_id;
        if (selectedId == null) return null;

        return (
            filteredRows.find(
                (r) => Number(r?.rmeliramacolors_szam) === Number(selectedId)
            ) || null
        );
    }, [filteredRows, order?.rama_id]);

    const [selectedTypeId, setSelectedTypeId] = useState(
        selectedFromOrder ? String(selectedFromOrder.rmeliramacolors_rama_type_id) : ""
    );
    const [selectedColorId, setSelectedColorId] = useState(
        selectedFromOrder ? String(selectedFromOrder.rmeliramacolors_sole_color_id) : ""
    );

    useEffect(() => {
        if (!selectedFromOrder) {
            setSelectedTypeId("");
            setSelectedColorId("");
            return;
        }

        setSelectedTypeId(String(selectedFromOrder.rmeliramacolors_rama_type_id));
        setSelectedColorId(String(selectedFromOrder.rmeliramacolors_sole_color_id));
    }, [selectedFromOrder]);

    const colorOptionsForSelectedType = useMemo(() => {
        if (!selectedTypeId) return [];

        const map = new Map();

        filteredRows
            .filter((row) => String(row?.rmeliramacolors_rama_type_id) === selectedTypeId)
            .forEach((row) => {
                const colorId = String(row?.rmeliramacolors_sole_color_id ?? "");
                const colorName = row?.rmeliramacolors_sole_color_national || "";
                if (!colorId || !colorName) return;

                if (!map.has(colorId)) {
                    map.set(colorId, { value: colorId, label: colorName });
                }
            });

        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "hu"));
    }, [filteredRows, selectedTypeId]);

    const findRamaRow = (typeId, colorId) => {
        return (
            filteredRows.find(
                (r) =>
                    String(r?.rmeliramacolors_rama_type_id) === String(typeId) &&
                    String(r?.rmeliramacolors_sole_color_id) === String(colorId)
            ) || null
        );
    };

    const handleTypeSelect = (type) => {
        const nextTypeId = String(type.value);
        setSelectedTypeId(nextTypeId);
        setSelectedColorId("");

        updateOrder((prev) => ({
            ...prev,
            rama_id: null
        }));
    };

    const handleColorSelect = (color) => {
        const nextColorId = String(color.value);
        const matchedRow = findRamaRow(selectedTypeId, nextColorId);
        const matchedSzam = matchedRow?.rmeliramacolors_szam != null ? Number(matchedRow.rmeliramacolors_szam) : null;

        setSelectedColorId(nextColorId);

        updateOrder((prev) => ({
            ...prev,
            rama_id: matchedSzam
        }));
    };

    const handleBack = () => {
        navigate("/Koztalp");
    };

    const handleNext = () => {
        navigate("/BelesTipus");
    };

    const canProceed = Boolean(order?.rama_id);

    return (
        <TablerLayout title="Ráma kiválasztása">
            <div className="row justify-content-center">
                <div className="col-md-10">
                    <div className="card-body">
                        <h5 className="text-lg font-semibold mb-3">Kérem válasszon ráma típust</h5>

                        {typeOptions.length === 0 ? (
                            <div className="alert alert-warning">
                                Nincs választható ráma típus ehhez a modellhez.
                            </div>
                        ) : (
                            <div className="row g-3 mb-4">
                                {typeOptions.map((type) => (
                                    <div className="col-12 col-sm-6 col-md-4" key={type.value}>
                                        <button
                                            type="button"
                                            className={
                                                "btn w-100 py-3 fw-semibold " +
                                                (selectedTypeId === type.value ? "btn-primary" : "btn-outline-primary text-primary border-primary bg-white")
                                            }
                                            style={{
                                                fontSize: 18,
                                                fontWeight: 600,
                                                borderWidth: 2,
                                                minHeight: 60,
                                            }}
                                            onClick={() => handleTypeSelect(type)}
                                        >
                                            {type.label}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <h5 className="text-lg font-semibold mb-3">Kérem válasszon ráma színt</h5>

                        {!selectedTypeId && (
                            <div className="alert alert-info">
                                Először válasszon ráma típust a színek megjelenítéséhez.
                            </div>
                        )}

                        {selectedTypeId && (
                            <div className="row g-3">
                                {colorOptionsForSelectedType.map((color) => (
                                    <div className="col-12 col-sm-6 col-md-4" key={color.value}>
                                        <button
                                            type="button"
                                            className={
                                                "btn w-100 py-3 fw-semibold " +
                                                (selectedColorId === color.value ? "btn-primary" : "btn-outline-primary text-primary border-primary bg-white")
                                            }
                                            style={{
                                                fontSize: 18,
                                                fontWeight: 600,
                                                borderWidth: 2,
                                                minHeight: 60,
                                            }}
                                            onClick={() => handleColorSelect(color)}
                                        >
                                            {color.label}
                                        </button>
                                    </div>
                                ))}

                                {colorOptionsForSelectedType.length === 0 && (
                                    <div className="col-12">
                                        <div className="alert alert-warning mb-0">
                                            Ehhez a ráma típushoz jelenleg nincs szín opció.
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="card-footer d-flex justify-content-between">
                        <StepperNavButton side="left" label="Vissza" onClick={handleBack} />
                        <StepperNavButton side="right" label="Tovább" onClick={handleNext} disabled={!canProceed} />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default Rama;